import React, { useState } from "react";
import {
  PrimaryBlueButton,
  SecondaryBlueButton,
  FormValidator,
  Severity
} from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import useTeams from "@/hooks/useTeams";
import useChatWindows from "@/hooks/useChatWindows";
import useToastAlert from "@/hooks/useToastAlert";
import Select from "@/components/Select";

interface Props {
  onClose(): void;
  onDeploymentAdded(): void;
}

export default function AddDeploymentModal({ onClose, onDeploymentAdded }: Props) {
  const api = useApi();
  const { teams } = useTeams();
  const { chatWindows } = useChatWindows();
  const toastAlert = useToastAlert();

  const [displayName, setDisplayName] = useState("");
  const [teamId, setTeamId] = useState<number>(
    Array.isArray(teams) && teams.length > 0 ? teams[0].id : 0
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  async function handleSubmit() {
    setErrors({});
    const newErrors: Record<string, string> = {};

    if (!displayName.trim()) {
      newErrors["DisplayName"] = "Display name is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      const response = await api.messaging.post("Deployment", {
        displayName: displayName.trim(),
        teamId
      });

      if (response.ok) {
        toastAlert.displayToast(Severity.Success, "Deployment created successfully");
        onDeploymentAdded();
      } else if (response.status === 400) {
        const errorResponse = await response.json();
        if (typeof errorResponse?.errors === "object") {
          setErrors(errorResponse.errors);
        }
        toastAlert.displayToast(Severity.Error, errorResponse?.message ?? "Failed to create deployment");
      } else if (response.status === 401) {
        toastAlert.displayToast(Severity.Error, "You do not have permission to create deployments");
      } else if (response.status === 402) {
        const body = await response.json().catch(() => null);
        toastAlert.displayToast(
          Severity.Error,
          body?.error ?? "Deployment limit reached. Contact your account manager to upgrade."
        );
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to create deployment");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-lg w-[500px] p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-1">
          Add Deployment
        </h2>
        <p className="text-sm text-slate-500 mb-6">
          Create a new chat entry point with its own embed code, default team, and settings.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Display Name
            </label>
            <FormValidator error={errors["DisplayName"]}>
              <input
                type="text"
                className="input w-full"
                placeholder="e.g. Support Portal, Sales Website"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                autoFocus
              />
            </FormValidator>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Default Team
            </label>
            <Select
              options={
                Array.isArray(teams)
                  ? teams.reduce(
                      (acc, team) => {
                        acc[team.id.toString()] = team.name;
                        return acc;
                      },
                      {} as Record<string, string>
                    )
                  : {}
              }
              value={teamId.toString()}
              onChange={(value) => setTeamId(Number(value))}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <SecondaryBlueButton label="Cancel" onClick={onClose} />
          <PrimaryBlueButton
            label={saving ? "Creating..." : "Create Deployment"}
            onClick={() => { if (!saving) handleSubmit(); }}
          />
        </div>
      </div>
    </div>
  );
}
